/**
 * Volleyball simulation.
 *
 * Fixed 120 Hz timestep, driven by an accumulator in the caller. The step is a
 * pure function of (state, inputs) apart from the Rapier world it owns: no
 * Date.now(), no Math.random(), no DOM.
 *
 * Rapier integrates the ball only (gravity, CCD against the static geometry).
 * Blobs are integrated by hand: a blob's motion must depend on nothing but its
 * own input, which is what makes client-side prediction correct later on.
 * Ball-vs-blob contact is scripted rather than solved, because the reflection
 * angle off the dome is the entire skill ceiling of the game.
 */
import RAPIER from '@dimforge/rapier2d-compat';
import { type Constants, DEFAULTS, SIM_DT, SIM_HZ, cloneConstants } from './constants.js';

export interface BlobInput {
  left: boolean;
  right: boolean;
  jump: boolean;
  attack: boolean;
}

export const NO_INPUT: BlobInput = Object.freeze({
  left: false,
  right: false,
  jump: false,
  attack: false,
});

/** 0 = left half of the court, 1 = right half. */
export type Side = 0 | 1;

export interface BlobState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  grounded: boolean;
  side: Side;
  smashCooldown: number;
  /** Sim tick of this blob's last ball touch; -1 if it has never touched it. */
  lastTouchTick: number;
  /** Smash energy, 0..1. Drains while smash is held, refills otherwise. */
  energy: number;
}

/** True while the blob is spending energy: any touch now is a powered hit. */
export function isCharged(blob: BlobState, input: BlobInput): boolean {
  return input.attack && blob.energy > 0;
}

export interface BallState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Angular velocity, rad/s, positive = counter-clockwise. */
  spin: number;
  /** Visual rotation, integrated from spin. Cosmetic only. */
  angle: number;
}

/**
 * `serve`  - ball parked over the server, counting down
 * `rally`  - live ball, scoring enabled
 * `point`  - a point just landed, short pause before the next serve
 * `over`   - match decided
 */
export type Phase = 'serve' | 'rally' | 'point' | 'over';

export type FaultReason = 'grounded' | 'touches' | null;

export interface MatchState {
  phase: Phase;
  /** Ticks remaining in a `serve` or `point` pause. */
  phaseTicks: number;
  score: [number, number];
  server: Side;
  /** Side that owns the current touch streak, or null when possession is open. */
  touchSide: Side | null;
  touches: number;
  lastPointWinner: Side | null;
  lastFault: FaultReason;
  winner: Side | null;
}

export interface SimState {
  tick: number;
  ball: BallState;
  blobs: BlobState[];
  match: MatchState;
}

export interface SimOptions {
  constants?: Constants;
  /** Blob sides, in order. One entry per blob. */
  sides?: Side[];
  /** Whether the net collider exists. Step 1 plays in an open box. */
  net?: boolean;
}

let rapierReady: Promise<void> | null = null;

/** Idempotent Rapier wasm init. Must be awaited before constructing a sim. */
export function initPhysics(): Promise<void> {
  rapierReady ??= RAPIER.init();
  return rapierReady;
}

const EPS = 1e-6;

/** The opposing side. */
export function other(side: Side): Side {
  return side === 0 ? 1 : 0;
}

/**
 * Advance one blob by one fixed step.
 *
 * Exported because a blob's motion depends on nothing but its own input, the
 * constants and the court bounds -- no ball, no physics world, no other player.
 * That is what makes a remote player able to predict their own blob exactly,
 * and it is why this is a free function rather than a method.
 */
export function stepBlobMotion(
  blob: BlobState,
  input: BlobInput,
  c: Constants,
  netEnabled: boolean,
): void {
  const dir = (input.right ? 1 : 0) - (input.left ? 1 : 0);
  const control = blob.grounded ? 1 : c.airControl;
  const target = dir * c.moveSpeed;
  const accel = c.blobAccel * control * SIM_DT;
  const dv = target - blob.vx;
  blob.vx += Math.abs(dv) <= accel ? dv : Math.sign(dv) * accel;

  if (input.jump && blob.grounded) {
    blob.vy = c.jumpImpulse;
    blob.grounded = false;
  }

  blob.vy += c.gravity * SIM_DT;
  blob.x += blob.vx * SIM_DT;
  blob.y += blob.vy * SIM_DT;

  if (blob.y <= 0) {
    blob.y = 0;
    blob.vy = 0;
    blob.grounded = true;
  }

  // Blobs stay on their own half; the net blocks crossing.
  const half = c.courtWidth / 2;
  const netHalf = c.netThickness / 2;
  let lo = c.blobRadius;
  let hi = c.courtWidth - c.blobRadius;
  if (netEnabled) {
    if (blob.side === 0) hi = half - netHalf - c.blobRadius;
    else lo = half + netHalf + c.blobRadius;
  }
  if (blob.x < lo) {
    blob.x = lo;
    if (blob.vx < 0) blob.vx = 0;
  } else if (blob.x > hi) {
    blob.x = hi;
    if (blob.vx > 0) blob.vx = 0;
  }

  if (blob.smashCooldown > 0) blob.smashCooldown = Math.max(0, blob.smashCooldown - SIM_DT);

  // The bar is part of blob motion on purpose: it depends on nothing but this
  // blob's own input, so a remote player predicts it exactly.
  if (input.attack) {
    blob.energy = Math.max(0, blob.energy - c.energyDrain * SIM_DT);
  } else {
    blob.energy = Math.min(1, blob.energy + c.energyRegen * SIM_DT);
  }
}

export class VolleySim {
  readonly constants: Constants;
  readonly blobs: BlobState[];
  readonly ball: BallState = { x: 0, y: 0, vx: 0, vy: 0, spin: 0, angle: 0 };
  readonly match: MatchState = {
    phase: 'serve',
    phaseTicks: 0,
    score: [0, 0],
    server: 0,
    touchSide: null,
    touches: 0,
    lastPointWinner: null,
    lastFault: null,
    winner: null,
  };
  tick = 0;

  /** Which half the ball was on last tick, for detecting a net crossing. */
  private ballSide: Side = 0;

  private world: RAPIER.World;
  private ballBody: RAPIER.RigidBody;
  private ballCollider: RAPIER.Collider;
  private staticBody: RAPIER.RigidBody;
  private staticColliders: RAPIER.Collider[] = [];
  private readonly netEnabled: boolean;

  /** Signature of the geometry currently baked into the world. */
  private geometrySig = '';
  private ballSig = '';

  constructor(opts: SimOptions = {}) {
    this.constants = cloneConstants(opts.constants ?? DEFAULTS);
    this.netEnabled = opts.net ?? false;
    const sides = opts.sides ?? ([0] as Side[]);

    const c = this.constants;
    this.world = new RAPIER.World({ x: 0, y: c.gravity });
    this.world.timestep = SIM_DT;

    this.staticBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    this.ballBody = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic().setCcdEnabled(true).setCanSleep(false),
    );
    this.ballCollider = this.world.createCollider(
      RAPIER.ColliderDesc.ball(c.ballRadius),
      this.ballBody,
    );

    this.blobs = sides.map((side) => ({
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      grounded: true,
      side,
      smashCooldown: 0,
      lastTouchTick: -1,
      energy: 1,
    }));

    this.syncGeometry();
    this.syncBall();
    this.reset();
  }

  /** Free the Rapier world. Call when tearing down a match. */
  dispose(): void {
    this.world.free();
  }

  /** Place blobs on their home spots and park the ball over `servingSide`. */
  reset(servingSide: Side = 0): void {
    const c = this.constants;
    const placed = [0, 0];
    for (const blob of this.blobs) {
      const total = this.blobs.filter((b) => b.side === blob.side).length;
      blob.x = this.laneX(blob.side, placed[blob.side]!++, total);
      blob.y = 0;
      blob.vx = 0;
      blob.vy = 0;
      blob.grounded = true;
      blob.smashCooldown = 0;
      blob.lastTouchTick = -1;
      blob.energy = 1;
    }
    const m = this.match;
    m.server = servingSide;
    m.phase = 'serve';
    m.phaseTicks = Math.round(c.serveDelay * SIM_HZ);
    m.touchSide = null;
    m.touches = 0;
    this.parkServe();
  }

  /** Reset the score as well as the court. */
  resetMatch(servingSide: Side = 0): void {
    const m = this.match;
    m.score = [0, 0];
    m.winner = null;
    m.lastPointWinner = null;
    m.lastFault = null;
    this.reset(servingSide);
  }

  /**
   * Home spot for the `index`-th blob of a side. Lanes sit in the net-side half
   * of each court so the serve, which drops near the sideline, is never a free
   * bounce off the server's own head.
   */
  private laneX(side: Side, index: number, total: number): number {
    const c = this.constants;
    const half = c.courtWidth / 2;
    const f = 0.45 + (0.5 * (index + 1)) / (total + 1);
    return side === 0 ? f * half : c.courtWidth - f * half;
  }

  private serveX(): number {
    const c = this.constants;
    const half = c.courtWidth / 2;
    return this.match.server === 0 ? half * 0.2 : c.courtWidth - half * 0.2;
  }

  private parkServe(): void {
    this.setBall(this.serveX(), this.constants.courtHeight * 0.8, 0, 0, 0);
    this.ballSide = this.match.server;
  }

  setBall(x: number, y: number, vx: number, vy: number, spin: number): void {
    this.ballBody.setTranslation({ x, y }, true);
    this.ballBody.setLinvel({ x: vx, y: vy }, true);
    this.ball.x = x;
    this.ball.y = y;
    this.ball.vx = vx;
    this.ball.vy = vy;
    this.ball.spin = spin;
    this.ball.angle = 0;
  }

  /** Snapshot of the current state. Nothing in it aliases the sim. */
  snapshot(): SimState {
    return {
      tick: this.tick,
      ball: { ...this.ball },
      blobs: this.blobs.map((b) => ({ ...b })),
      match: { ...this.match, score: [this.match.score[0], this.match.score[1]] },
    };
  }

  /**
   * Advance exactly one fixed timestep.
   * `inputs[i]` drives `blobs[i]`; missing entries are treated as idle.
   */
  step(inputs: readonly BlobInput[]): void {
    const c = this.constants;
    const m = this.match;
    this.syncGeometry();
    this.syncBall();
    this.world.gravity = { x: 0, y: c.gravity };

    // Blobs always move: warming up between points is half the fun.
    for (let i = 0; i < this.blobs.length; i++) {
      this.stepBlob(this.blobs[i]!, inputs[i] ?? NO_INPUT);
    }

    if (m.phase === 'serve') {
      // The ball hangs over the server until the countdown expires. Physics is
      // skipped entirely so the parked position is exact rather than jittering
      // by one tick of gravity.
      this.setBall(this.serveX(), c.courtHeight * 0.8, 0, 0, 0);
      this.tick++;
      this.advancePhase();
      return;
    }

    const live = m.phase === 'rally';

    this.readBall();
    this.applyMagnus();
    for (let i = 0; i < this.blobs.length; i++) {
      const blob = this.blobs[i]!;
      if (this.resolveContact(blob, inputs[i] ?? NO_INPUT) && live) {
        this.registerTouch(blob);
      }
    }
    this.clampBallSpeed();
    this.writeBall();

    this.world.step();
    this.tick++;
    this.readBall();
    this.ball.angle += this.ball.spin * SIM_DT;

    this.updateBallSide();
    if (m.phase === 'rally') this.checkGrounded();
    this.advancePhase();
  }

  // --- rules --------------------------------------------------------------

  /**
   * Count a touch against the hitting side. Consecutive touches by one blob are
   * legal, but the whole side only gets `maxTouches` before the ball is dead.
   */
  private registerTouch(blob: BlobState): void {
    const c = this.constants;
    const m = this.match;
    if (m.touchSide === blob.side) {
      m.touches++;
    } else {
      m.touchSide = blob.side;
      m.touches = 1;
    }
    if (m.touches > c.maxTouches) {
      this.awardPoint(other(blob.side), 'touches');
    }
  }

  /** Possession resets whenever the ball crosses the net. */
  private updateBallSide(): void {
    const side: Side = this.ball.x < this.constants.courtWidth / 2 ? 0 : 1;
    if (side !== this.ballSide) {
      this.ballSide = side;
      this.match.touchSide = null;
      this.match.touches = 0;
    }
  }

  /** A ball on the floor is dead: the point goes to the other side. */
  private checkGrounded(): void {
    if (this.ball.y > this.constants.ballRadius + 0.02) return;
    const landedOn: Side = this.ball.x < this.constants.courtWidth / 2 ? 0 : 1;
    this.awardPoint(other(landedOn), 'grounded');
  }

  private awardPoint(winner: Side, reason: FaultReason): void {
    const c = this.constants;
    const m = this.match;
    if (m.phase !== 'rally') return;
    m.score[winner]++;
    m.lastPointWinner = winner;
    m.lastFault = reason;
    m.server = winner;
    m.touchSide = null;
    m.touches = 0;

    const mine = m.score[winner];
    const theirs = m.score[other(winner)];
    if (mine >= c.pointsToWin && mine - theirs >= c.winBy) {
      m.winner = winner;
      m.phase = 'over';
      m.phaseTicks = 0;
      return;
    }
    m.phase = 'point';
    m.phaseTicks = Math.round(c.pointDelay * SIM_HZ);
  }

  /** Drive the serve/point countdowns. */
  private advancePhase(): void {
    const c = this.constants;
    const m = this.match;
    if (m.phase === 'rally' || m.phase === 'over') return;
    if (m.phaseTicks > 0) {
      m.phaseTicks--;
      return;
    }
    if (m.phase === 'point') {
      this.reset(m.server);
      m.phaseTicks = Math.round(c.serveDelay * SIM_HZ);
      return;
    }
    // Serve countdown finished: release the ball.
    m.phase = 'rally';
    m.touchSide = null;
    m.touches = 0;
  }

  // --- blob ---------------------------------------------------------------

  private stepBlob(blob: BlobState, input: BlobInput): void {
    stepBlobMotion(blob, input, this.constants, this.netEnabled);
  }

  // --- ball ---------------------------------------------------------------

  private readBall(): void {
    const t = this.ballBody.translation();
    const v = this.ballBody.linvel();
    this.ball.x = t.x;
    this.ball.y = t.y;
    this.ball.vx = v.x;
    this.ball.vy = v.y;
  }

  private writeBall(): void {
    this.ballBody.setTranslation({ x: this.ball.x, y: this.ball.y }, true);
    this.ballBody.setLinvel({ x: this.ball.vx, y: this.ball.vy }, true);
  }

  private applyMagnus(): void {
    const c = this.constants;
    const b = this.ball;
    // a = k * (omega x v), which in 2D is k * omega * perp(v).
    const ax = -c.magnusCoefficient * b.spin * b.vy;
    const ay = c.magnusCoefficient * b.spin * b.vx;
    b.vx += ax * SIM_DT;
    b.vy += ay * SIM_DT;
    b.spin *= Math.max(0, 1 - c.spinDamping * SIM_DT);
  }

  private clampBallSpeed(): void {
    const c = this.constants;
    const b = this.ball;
    const speed = Math.hypot(b.vx, b.vy);
    if (speed > c.ballMaxSpeed) {
      const k = c.ballMaxSpeed / speed;
      b.vx *= k;
      b.vy *= k;
    }
  }

  // --- contact ------------------------------------------------------------

  /**
   * A blob is a half-disc: dome above `blob.y`, flat underside at `blob.y`.
   * Returns true if this blob hit the ball on this tick.
   */
  private resolveContact(blob: BlobState, input: BlobInput): boolean {
    const c = this.constants;
    const b = this.ball;
    const R = c.blobRadius;
    const r = c.ballRadius;

    const dx = b.x - blob.x;
    const dy = b.y - blob.y;

    let nx: number;
    let ny: number;
    let pen: number;

    if (dy >= 0) {
      const dist = Math.hypot(dx, dy) || EPS;
      nx = dx / dist;
      ny = dy / dist;
      pen = R + r - dist;
    } else {
      const qx = Math.min(Math.max(b.x, blob.x - R), blob.x + R);
      const ex = b.x - qx;
      const dist = Math.hypot(ex, dy) || EPS;
      nx = ex / dist;
      ny = dy / dist;
      pen = r - dist;
    }

    const facing = blob.side === 0 ? 1 : -1;
    const centreDist = Math.hypot(dx, dy);

    // Charged: the bar is draining, so the next touch is a powered one. In the
    // air above the threshold that is the classic spike; anywhere else it is
    // the ordinary reflection geometry at smash speed.
    const charged = isCharged(blob, input) && blob.smashCooldown <= 0;
    const inRange = centreDist <= R + r + c.smashRange;

    if (charged && inRange && !blob.grounded && blob.y >= c.smashMinHeight) {
      const a = (c.smashAngleDeg * Math.PI) / 180;
      b.vx = Math.cos(a) * c.smashImpulse * facing;
      b.vy = -Math.sin(a) * c.smashImpulse;
      b.spin += -facing * c.spinTransfer * 20;
      blob.smashCooldown = c.smashCooldown;
      if (pen > 0) {
        b.x += nx * (pen + 0.001);
        b.y += ny * (pen + 0.001);
      }
      return this.stampTouch(blob);
    }

    if (pen <= 0) return false;

    // Depenetrate first so the ball can never rest inside the dome.
    b.x += nx * (pen + 0.001);
    b.y += ny * (pen + 0.001);

    const rvx = b.vx - blob.vx;
    const rvy = b.vy - blob.vy;
    const vn = rvx * nx + rvy * ny;
    if (vn > 0) return false; // already separating

    const incoming = Math.hypot(b.vx, b.vy);
    const powered = charged && inRange;
    const outSpeed = powered ? c.smashImpulse : Math.max(c.hitSpeed, incoming * c.hitRestitution);
    const vt = -rvy * nx + rvx * ny; // relative velocity along the tangent

    b.vx = nx * outSpeed + blob.vx * c.blobVelTransfer;
    b.vy = ny * outSpeed + blob.vy * c.blobVelTransfer;
    b.spin += -vt * c.spinTransfer * (powered ? 2 : 1);
    if (powered) blob.smashCooldown = c.smashCooldown;
    return this.stampTouch(blob);
  }

  /**
   * Record a contact and report whether it counts as a fresh touch. A ball
   * grazing along the dome can produce several impulses in a row; the lockout
   * keeps those from burning the whole three-touch allowance in a few ticks.
   */
  private stampTouch(blob: BlobState): boolean {
    const lockout = Math.round(this.constants.touchLockout * SIM_HZ);
    const fresh = blob.lastTouchTick < 0 || this.tick - blob.lastTouchTick >= lockout;
    blob.lastTouchTick = this.tick;
    return fresh;
  }

  // --- world sync ---------------------------------------------------------

  /** Rebuild static geometry when a court constant changes under a slider. */
  private syncGeometry(): void {
    const c = this.constants;
    const sig = [
      c.courtWidth,
      c.courtHeight,
      c.netHeight,
      c.netThickness,
      c.ballRestitution,
      c.ballFriction,
      this.netEnabled,
    ].join(':');
    if (sig === this.geometrySig) return;
    this.geometrySig = sig;

    for (const col of this.staticColliders) this.world.removeCollider(col, false);
    this.staticColliders = [];

    const W = c.courtWidth;
    const H = c.courtHeight;
    const t = 1; // wall half-thickness: thick enough that CCD never misses
    const add = (hx: number, hy: number, x: number, y: number): void => {
      const desc = RAPIER.ColliderDesc.cuboid(hx, hy)
        .setTranslation(x, y)
        .setRestitution(c.ballRestitution)
        .setFriction(c.ballFriction);
      this.staticColliders.push(this.world.createCollider(desc, this.staticBody));
    };

    add(W / 2 + t, t, W / 2, -t); // floor
    add(t, H / 2 + t, -t, H / 2); // left wall
    add(t, H / 2 + t, W + t, H / 2); // right wall
    add(W / 2 + t, t, W / 2, H + t); // ceiling

    if (this.netEnabled) {
      add(c.netThickness / 2, c.netHeight / 2, W / 2, c.netHeight / 2);
    }
  }

  /** Rebuild the ball collider when its radius or surface constants change. */
  private syncBall(): void {
    const c = this.constants;
    const sig = [c.ballRadius, c.ballRestitution, c.ballFriction, c.ballLinearDamping].join(':');
    if (sig === this.ballSig) return;
    this.ballSig = sig;

    this.ballBody.setLinearDamping(c.ballLinearDamping);
    this.world.removeCollider(this.ballCollider, false);
    this.ballCollider = this.world.createCollider(
      RAPIER.ColliderDesc.ball(c.ballRadius)
        .setRestitution(c.ballRestitution)
        .setFriction(c.ballFriction),
      this.ballBody,
    );
  }
}
